import { useState, useEffect } from 'react'
import {
    Container,
    Grid,
    Card,
    CardMedia,
    Typography,
    Button,
    Avatar,
    Box,
    Chip,
    styled,
    useTheme,
    useMediaQuery
} from '@mui/material'
import {
    CalendarToday,
    Schedule,
    LocationOn,
    Star,
    Group
} from '@mui/icons-material'
import { useLocation } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'

// Luxurious color palette
const primaryColor = '#FF6D00'
const secondaryColor = '#FF9100'
const darkOrange = '#E65100'
const bgColor = '#FFF8F2'
const goldAccent = '#FFD700'
const darkText = '#2D2D2D'
const lightText = '#5A5A5A'

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: '16px',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    boxShadow: `0 4px 20px rgba(0, 0, 0, 0.08)`,
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: `0 12px 28px rgba(0, 0, 0, 0.15)`,
    },
    [theme.breakpoints.up('md')]: {
        display: 'flex',
        height: '280px',
    },
}))

const CourseMedia = styled(CardMedia)(({ theme }) => ({
    height: '240px',
    position: 'relative',
    [theme.breakpoints.up('md')]: {
        height: '100%',
        minWidth: '320px',
        width: '320px',
    },
    [theme.breakpoints.down('md')]: {
        height: '200px',
        width: '100%',
    },
}))

const OnlineBadge = styled(Chip)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: '#4CAF50',
    color: 'white',
    fontWeight: '600',
    fontSize: '0.75rem',
    padding: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.65rem',
        padding: theme.spacing(0.25),
    },
}))

const OfflineBadge = styled(Chip)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: primaryColor,
    color: 'white',
    fontWeight: '600',
    fontSize: '0.75rem',
    padding: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.65rem',
        padding: theme.spacing(0.25),
    },
}))

const PriceTag = styled('div')(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
    borderRadius: '20px',
    fontWeight: '700',
    color: darkOrange,
    fontSize: '0.9rem',
    boxShadow: `0 2px 8px rgba(0, 0, 0, 0.1)`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.8rem',
        padding: `${theme.spacing(0.3)} ${theme.spacing(1)}`,
    },
}))

const WorkshopDetail = () => {
    const theme = useTheme()
    const location = useLocation()
    const { t, language } = useLanguage()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const [courses, setCourses] = useState(location.state?.filteredCourses || [])
    const [filteredCourses, setFilteredCourses] = useState(location.state?.filteredCourses || [])


    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
        if (location.state?.filteredCourses) {
            setCourses(location.state.filteredCourses)
            setFilteredCourses(location.state.filteredCourses)
        }
    }, [location.state])

    const CountdownTimer = ({ eventDate }) => {
        const [timeLeft, setTimeLeft] = useState({
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0
        })

        useEffect(() => {
            const timer = setInterval(() => {
                const now = new Date()
                const target = new Date(eventDate)
                const difference = target - now

                if (difference <= 0) {
                    clearInterval(timer)
                    return
                }

                const days = Math.floor(difference / (1000 * 60 * 60 * 24))
                const hours = Math.floor((difference / (1000 * 60 * 60)) % 24)
                const minutes = Math.floor((difference / 1000 / 60) % 60)
                const seconds = Math.floor((difference / 1000) % 60)

                setTimeLeft({ days, hours, minutes, seconds })
            }, 1000)

            return () => clearInterval(timer)
        }, [eventDate])

        if (timeLeft.days > 0) {
            return (
                <Chip
                    label={t('workshopPage.startsInDays', { days: timeLeft.days, hours: timeLeft.hours })}
                    size="small"
                    sx={{
                        backgroundColor: primaryColor,
                        color: 'white',
                        fontWeight: '600',
                        height: '24px',
                        mb: 1,
                        fontSize: isSmallMobile ? '0.6rem' : '0.7rem'
                    }}
                />
            )
        } else if (timeLeft.hours > 0) {
            return (
                <Chip
                    label={t('workshopPage.startsInHours', { hours: timeLeft.hours, minutes: timeLeft.minutes })}
                    size="small"
                    sx={{
                        backgroundColor: '#E53935',
                        color: 'white',
                        fontWeight: '600',
                        height: '24px',
                        mb: 1,
                        fontSize: isSmallMobile ? '0.6rem' : '0.7rem'
                    }}
                />
            )
        } else {
            return (
                <Chip
                    label={t('workshopPage.startingSoon')}
                    size="small"
                    sx={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        fontWeight: '600',
                        height: '24px',
                        mb: 1,
                        fontSize: isSmallMobile ? '0.6rem' : '0.7rem'
                    }}
                />
            )
        }
    }

    return (
        <Container maxWidth="xl" sx={{
            backgroundColor: bgColor,
            py: 4,
            px: isSmallMobile ? 2 : isMobile ? 3 : 4,
            margin: 0,
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <Box sx={{
                maxWidth: 'lg',
                margin: '0 auto',
                width: '100%'
            }}>
                <Typography variant="h2" component="h1" gutterBottom sx={{
                    fontWeight: 700,
                    fontSize: isSmallMobile ? '1.5rem' : { xs: '1.75rem', md: '2rem' },
                    mb: 4,
                }}>
                    {location.state?.title || t('workshopPage.allWorkshops')}
                </Typography>

                {filteredCourses.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center',
                        padding: 4,
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        boxShadow: `0 4px 20px rgba(0, 0, 0, 0.08)`,
                        maxWidth: 'lg',
                        margin: '0 auto'
                    }}>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: darkText }}>
                            {t('workshopPage.noCoursesAvailable')}
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={isSmallMobile ? 2 : 4} sx={{ width: '100%', margin: 0 }}>
                        {filteredCourses.map((course) => (
                            <Grid item key={course.id} xs={12} sx={{ width: '100%' }}>
                                <StyledCard sx={{ width: '100%' }}>
                                    {/* Image on the left for desktop, top for mobile */}
                                    <Box sx={{
                                        position: 'relative',
                                        flexShrink: 0,
                                        width: isMobile ? '100%' : '320px'
                                    }}>
                                        <CourseMedia
                                            image={course.image}
                                            title={course.title}
                                        />
                                        <PriceTag>
                                            {course.discount ? (
                                                <>
                                                    <span style={{
                                                        textDecoration: 'line-through',
                                                        opacity: 0.7,
                                                        fontSize: isSmallMobile ? '0.7rem' : '0.8rem'
                                                    }}>
                                                        {course.price}
                                                    </span>
                                                    <span style={{
                                                        color: '#E53935',
                                                        fontWeight: '800',
                                                        fontSize: isSmallMobile ? '0.9rem' : '1.1rem'
                                                    }}>
                                                        {course.discountedPrice}
                                                    </span>
                                                    <span style={{
                                                        fontSize: isSmallMobile ? '0.6rem' : '0.7rem',
                                                        fontWeight: '600',
                                                        color: '#E53935',
                                                        marginTop: '2px'
                                                    }}>
                                                        {course.discount.percentage} {t('workshopPage.off')}
                                                    </span>
                                                </>
                                            ) : (
                                                <span style={{ fontSize: isSmallMobile ? '0.9rem' : '1rem' }}>
                                                    {course.price}
                                                </span>
                                            )}
                                        </PriceTag>
                                        {course.type === 'online' ? (
                                            <OnlineBadge label={t('workshopPage.virtualBadge')} />
                                        ) : (
                                            <OfflineBadge label={t('workshopPage.inPersonBadge')} />
                                        )}
                                    </Box>

                                    {/* Content in the middle */}
                                    <Box sx={{
                                        p: isSmallMobile ? 2 : 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1,
                                        justifyContent: 'space-between',
                                        width: isMobile ? '100%' : 'calc(100% - 320px)'
                                    }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? 1 : 2 }}>
                                            {!isNaN(Date.parse(course.date)) && <CountdownTimer eventDate={course.date} />}

                                            <Typography
                                                variant="h5"
                                                component="h2"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: darkText,
                                                    fontSize: isSmallMobile ? '1.25rem' : '1.5rem',
                                                    lineHeight: 1.3
                                                }}
                                            >
                                                {course.title}
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    src={course.instructor.avatar}
                                                    alt={course.instructor.name}
                                                    sx={{
                                                        width: isSmallMobile ? 32 : 36,
                                                        height: isSmallMobile ? 32 : 36,
                                                        border: `2px solid ${primaryColor}`
                                                    }}
                                                />
                                                <Box>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight="600"
                                                        sx={{ fontSize: isSmallMobile ? '0.9rem' : '1rem' }}
                                                    >
                                                        {course.instructor.name}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Star sx={{
                                                            color: goldAccent,
                                                            fontSize: isSmallMobile ? '0.8rem' : '1rem'
                                                        }} />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontSize: isSmallMobile ? '0.8rem' : '0.9rem' }}
                                                        >
                                                            {course.instructor.rating} • <Group sx={{
                                                                fontSize: isSmallMobile ? '0.7rem' : '0.8rem'
                                                            }} /> {course.seats}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: lightText,
                                                    fontSize: isSmallMobile ? '0.85rem' : '0.95rem',
                                                    lineHeight: 1.6
                                                }}
                                            >
                                                {course.description}
                                            </Typography>

                                            <Box sx={{
                                                display: 'grid',
                                                gridTemplateColumns: isSmallMobile ? '1fr' : 'repeat(3, 1fr)',
                                                gap: isSmallMobile ? 1 : 2,
                                                mt: 1
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CalendarToday
                                                        fontSize={isSmallMobile ? 'small' : 'medium'}
                                                        sx={{ color: primaryColor }}
                                                    />
                                                    <Typography variant="body2" sx={{ fontSize: isSmallMobile ? '0.8rem' : '0.9rem' }}>
                                                        {!isNaN(Date.parse(course.date)) ? new Date(course.date).toLocaleDateString(language, {
                                                            weekday: 'short',
                                                            month: 'short',
                                                            day: 'numeric',
                                                            year: isSmallMobile ? undefined : 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        }) : t('workshopPage.comingSoon')}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Schedule
                                                        fontSize={isSmallMobile ? 'small' : 'medium'}
                                                        sx={{ color: primaryColor }}
                                                    />
                                                    <Typography variant="body2" sx={{ fontSize: isSmallMobile ? '0.8rem' : '0.9rem' }}>
                                                        {course.time}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LocationOn
                                                        fontSize={isSmallMobile ? 'small' : 'medium'}
                                                        sx={{ color: primaryColor }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: isSmallMobile ? '0.8rem' : '0.9rem',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {course.venue}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Reserve button at the bottom */}
                                        <Box sx={{
                                            pt: 2,
                                            borderTop: `1px solid ${theme.palette.divider}`,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: 2
                                        }}>
                                            {course.discountedPrice && (
                                                <Chip
                                                    label={t('workshopPage.limited')}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#E53935',
                                                        color: 'white',
                                                        fontWeight: '600',
                                                        fontSize: isSmallMobile ? '0.6rem' : '0.7rem'
                                                    }}
                                                />
                                            )}
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                href={course.registrationUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    py: isSmallMobile ? 1 : 1.25,
                                                    fontWeight: 600,
                                                    fontSize: isSmallMobile ? '0.8rem' : '0.9rem',
                                                    background: `linear-gradient(45deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                                                    '&:hover': {
                                                        background: `linear-gradient(45deg, ${darkOrange} 0%, ${primaryColor} 100%)`,
                                                    }
                                                }}
                                            >
                                                {t('workshopPage.reserve')}
                                            </Button>
                                        </Box>
                                    </Box>
                                </StyledCard>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Container>
    )
}

export default WorkshopDetail